import {Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MachineCommandsEnum} from "../../enums/machine-commands.enum";
import {MatIcon, MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {DatePipe} from "@angular/common";
import {ConfirmationDialogComponent, getMe, State, UserModel} from "@overflo1/frame-frontend";
import {MatTabsModule} from "@angular/material/tabs";
import {MatSelectModule} from "@angular/material/select";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatAutocompleteModule} from "@angular/material/autocomplete";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MachineCommandsIconEnum} from "../../enums/machine-commands-icon.enum";
import {MachineService} from "../../services/machine/machine.service";
import {ActivatedRoute} from "@angular/router";
import {MatTableModule} from "@angular/material/table";
import {MatPaginatorModule, PageEvent} from "@angular/material/paginator";
import {MatDialog} from "@angular/material/dialog";
import {MatSortModule} from "@angular/material/sort";
import dayjs from "dayjs";
import {MachineCommandType} from "../../models/machine-command.type";
import {Store} from "@ngrx/store";
import {MachineCommandStateEnum} from "../../enums/machine-command-state.enum";
import {INSTALLATION_TYPES, InstallationType} from '../../models/installation-type.type';

@Component({
  selector: 'app-machine-commands',
  templateUrl: './machine-commands.component.html',
  styleUrl: './machine-commands.component.scss',
  standalone: true,
  imports: [
    MatIcon,
    MatButtonModule,
    DatePipe,
    MatTabsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule
  ]
})
export class MachineCommandsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  protected readonly Object = Object;
  protected readonly MachineCommandsIconEnum = MachineCommandsIconEnum;
  protected readonly MachineCommandsEnum = MachineCommandsEnum;
  protected readonly MachineCommandStateEnum = MachineCommandStateEnum;
  @Input() installationType?: InstallationType;

  private static readonly SCREEN_TOGGLE_COMMANDS_BY_INSTALLATION_TYPE: Record<
    InstallationType,
    [keyof typeof MachineCommandsEnum, keyof typeof MachineCommandsEnum]
  > = {
    projector: ['switchOnProjector', 'switchOffProjector'],
    monitor: ['switchOnScreen', 'switchOffScreen'],
  };

  get machineCommandsMapped() {
    const type: InstallationType =
      this.installationType && INSTALLATION_TYPES.includes(this.installationType)
        ? this.installationType
        : 'projector';
    const keys: (keyof typeof MachineCommandsEnum)[] = [
      'reboot',
      'shutdown',
      'reloadScheduler',
      ...MachineCommandsComponent.SCREEN_TOGGLE_COMMANDS_BY_INSTALLATION_TYPE[type],
    ];
    return keys.map(key => ({key, value: MachineCommandsEnum[key]}));
  }

  machineId: number;
  commandsButtonsDisabled: boolean = false;
  loggedUser?: UserModel;

  commandsHistory: MachineCommandType[] = [];
  totalCommands = 0;
  displayedColumns = ['command', 'state', 'sentBy', 'sentAt'];
  pageSize = 10;
  pageIndex = 0;
  sortField = 'sentAt';
  sortDir: 'asc' | 'desc' = 'desc';

  constructor(
    private readonly machineService: MachineService,
    private readonly route: ActivatedRoute,
    private readonly matDialog: MatDialog,
    private store: Store<State>
  ) {

  }

  async ngOnInit() {
    this.store.select(getMe).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => this.loggedUser = user)
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (params) => {
      const entityId = params?.['entityId'];
      if (entityId && entityId !== 'create') {
        this.machineId = Number(entityId);
        await this.loadCommandsHistory();
      }
    });
  }

  async loadCommandsHistory() {
    const result = await this.machineService.getCommandsHistory(
      this.machineId,
      this.pageSize,
      this.pageIndex * this.pageSize,
      this.sortField,
      this.sortDir,
    );
    this.checkAndDisableCommandsButton(result.data)
    this.totalCommands = result.total;
  }

  checkAndDisableCommandsButton(data: MachineCommandType[]) {
    if (!data.length) {
      this.commandsButtonsDisabled = false;
    } else {
      this.commandsHistory = data.sort((a, b) => {
        if (!a.sentAt || !b.sentAt) return 1;
        return dayjs(a.sentAt).isAfter(b.sentAt) ? -1 : 1;
      });
      this.commandsButtonsDisabled = this.commandsHistory[0].state === 'pending';

    }
  }

  async cancelCommand() {
    const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
      data: {title: `Conferma annullamento`, description: `Sei sicuro di voler annullare il comando in esecuzione?`},
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async result => {
      if (!result) return;
      await this.machineService.cancelPendingCommand(this.machineId);
      await this.loadCommandsHistory();
    })
  }

  async sendCommand(command: string) {
    const dialogRef = this.matDialog.open(ConfirmationDialogComponent, {
      data: {title: `Conferma comando`, description: `Sei sicuro di voler eseguire il comando: ${MachineCommandsEnum[command]}?`},
    });
    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async result => {
      if (!result) return;
      this.commandsButtonsDisabled = true
      await this.machineService.sendCommand({
        machineId: this.machineId,
        command,
        sentBy: this.loggedUser?.email ?? '',
      });
      this.pageIndex = 0;
      await this.loadCommandsHistory();
    })
  }

  async onPageChange(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;
    await this.loadCommandsHistory();
  }

}
